import {
  useMsal,
  UnauthenticatedTemplate,
  AuthenticatedTemplate,
  useIsAuthenticated,
} from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./auth/auth-config";
import { TokenContext } from "./contexts/tokenContext";
import { UserContext } from "./contexts/UserContext";
import DialogBox from "components/DialogBox/DialogBox";
import UnAuthenticatedModal from "components/UnauthenticatedModal/UnAuthenticatedModal";
import { RouterProvider } from "react-router";
import { routesConfig } from "./utils/routesConfig";
import { useQuery } from "@tanstack/react-query";
import { createInstance } from "./utils/axios";
import { APIS, defaultUserClaims } from "./utils/constants";
import { isFeatureEnabled, featureFlags } from "./utils/featureFlag";
import { NotificationProvider } from "./contexts/NotificationProvider";
import { PermissionsProvider } from "./contexts/PermissionsProvider";
import FeedbackButton from "./components/Feedback/FeedbackButton";
import { warmApis } from "./utils/warmupApis";

/**
 * Dev-mode bypass: when VITE_LOCAL_MODE=true (set in .env.local), skip
 * MSAL entirely and treat the session as authenticated with a stub
 * token + hardcoded user. The BE's BannerFilterMiddleware grants all
 * banners when no real bearer is supplied (same dev path).
 */
const LOCAL_MODE_BYPASS = (import.meta.env.VITE_LOCAL_MODE as string) === "true";
const LOCAL_DEV_TOKEN = "local-dev-bypass-token";
const LOCAL_DEV_USERNAME = "Christopher Golden";

const App = () => {
  const [userName, setUserName] = useState<string>(
    LOCAL_MODE_BYPASS ? LOCAL_DEV_USERNAME : "",
  );
  const { accounts, instance: msalInstance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [accessToken, setAccessToken] = useState<string>(
    LOCAL_MODE_BYPASS ? LOCAL_DEV_TOKEN : "",
  );
  const isFeatureUserClaimsEnabled = isFeatureEnabled(featureFlags.UserClaims);

  const tokenObj = useMemo(
    () => ({
      accessToken,
      setAccessToken,
    }),
    [accessToken, setAccessToken]
  );

  const { data: userInfo } = useQuery({
    queryKey: ["userinfo"],
    retry: false, // Do not retry on failure
    queryFn: async () => {
      try {
        const axiosInstance = createInstance(accessToken, setAccessToken);
        const response = await axiosInstance.get(APIS.UserClaims);
        const claimsArray = (await response.data.claims) || [];
        const claimsObject = claimsArray.reduce(
          (
            acc: Record<string, string | string[]>,
            curr: { type: string; value: string }
          ) => {
            if (
              curr.type ===
              "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ) {
              if (!Array.isArray(acc["claims"])) {
                acc["claims"] = [];
              }
              (acc["claims"] as string[]).push(curr.value);
            } else {
              acc[curr.type] = curr.value;
            }
            return acc;
          },
          {}
        );
        return claimsObject;
      } catch (error) {
        return defaultUserClaims; // Return default user claims if there's an error fetching user claims
      }
    },
    enabled: isFeatureUserClaimsEnabled && !!accessToken, // Only fetch user info if the feature is enabled and accessToken is available
  });

  const userClaims = useMemo(() => {
    // Provide default values for all required UserInterface properties
    if (!userInfo) {
      return defaultUserClaims;
    }
    return {
      ...defaultUserClaims,
      ...userInfo,
    };
  }, [userInfo]);

  // Warm the APIs in the background so the first real request lands on an
  // already-primed app pool and Oracle connection rather than paying the cold
  // start. Runs once a token exists, because the published HealthCheck
  // operations inherit the gateway's Bearer security. Also re-warms when the
  // tab regains focus, since an app pool can go idle while the user is away.
  // Internally this waits for an idle moment and then warms one API at a time:
  // every RP API is behind the same APIM host, so a parallel burst would take
  // its connection slots away from the queries the screen actually needs.
  useEffect(() => {
    if (!accessToken) return;
    warmApis(accessToken);
    const onVisible = () => {
      if (document.visibilityState === "visible") warmApis(accessToken);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [accessToken]);

  useEffect(() => {
    // Dev bypass — already populated synchronously above; skip MSAL flow.
    if (LOCAL_MODE_BYPASS) return;
    if (!accounts || inProgress !== InteractionStatus.None) return;
    if (isAuthenticated) {
      const { username, name } = accounts[0];
      setUserName(name ?? username);
      msalInstance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          setAccessToken(response.accessToken);
        })
        .catch((error) => {
          console.log({ error });
          msalInstance.loginRedirect(loginRequest);
        });
    }
  }, [msalInstance, isAuthenticated, accounts, accounts.length, inProgress]);

  const authenticatedView = accessToken && (!isFeatureUserClaimsEnabled || userInfo || LOCAL_MODE_BYPASS) ? (
    <TokenContext.Provider value={tokenObj}>
      <UserContext.Provider value={userClaims}>
        <PermissionsProvider>
          <NotificationProvider>
            <RouterProvider router={routesConfig(userName)} />
            <FeedbackButton />
          </NotificationProvider>
        </PermissionsProvider>
      </UserContext.Provider>
    </TokenContext.Provider>
  ) : null;

  if (LOCAL_MODE_BYPASS) {
    // Dev bypass: skip MSAL <Authenticated|Unauthenticated>Template gating
    // and render the routes directly. The stub token from above flows
    // through the axios instance as the Bearer header.
    return <div>{authenticatedView}</div>;
  }

  return (
    <div>
      {/* Render the authenticated template only if the user is authenticated and has an access token
      If the feature flag for UserClaims is enabled, we will also check if userInfo is available
      If the feature flag is not enabled, we will render the authenticated template with the default userClaims context */}
      {authenticatedView && (
        <AuthenticatedTemplate>{authenticatedView}</AuthenticatedTemplate>
      )}
      <UnauthenticatedTemplate>
        <DialogBox isOpen={true}>
          <UnAuthenticatedModal
            handleLogin={() => msalInstance.loginRedirect(loginRequest)}
          />
        </DialogBox>
      </UnauthenticatedTemplate>
    </div>
  );
};

export default App;
